import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LinkElement } from '@els/els-react--link-element';
import { RsRoute } from '@Pages/RetroSynthesis/routes';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import history from '@AppRoutes/history';
import { searchReactionResultsByReactionId } from '../ConditionsReactionResultsService';
import { validateRetrosynthesisRouteSource } from '../ConditionsService';
import { getReaxysSourceLabel, getRetrosynthesisSourceLabel } from '../conditionsSourceLabel';

function SourcePill({ children, modifier }) {
  return (
    <>
      <span className={`rx-source-pill rx-source-pill--${modifier}`}>{children}</span>
      <br />
    </>
  );
}

SourcePill.propTypes = {
  children: PropTypes.node.isRequired,
  modifier: PropTypes.string.isRequired,
};

function ConditionsSourceLink({
  run,
  className = '',
  showSourcePill = false,
  linkProps = {},
  reaxysDataE2e = 'e2e-conditions-open-reaction-results',
  retrosynthesisDataE2e = 'e2e-conditions-open-rt-source',
  onBusyChange,
  onBeforeNavigate,
  onSourceClick,
}) {
  const [isSourceLoading, setSourceLoading] = useState(false);

  const setBusy = (isBusy) => {
    setSourceLoading(isBusy);
    onBusyChange?.(isBusy);
  };

  const openReaxysSource = async () => {
    if (isSourceLoading || !run.sourceReactionId) return;
    onSourceClick?.();
    setBusy(true);
    try {
      const { pathName, stateParams } = await searchReactionResultsByReactionId(run.sourceReactionId);
      setBusy(false);
      onBeforeNavigate?.(`conditions-source-reaxys-${run.id}`);
      history.push({ pathname: pathName, state: stateParams });
    } catch (error) {
      Logger.error('ConditionsSourceLink open Reaxys source failed with error:', error);
      NotifyDialogsService.openModalWindow({
        type: 'error',
        title: 'Reaxys reaction results unavailable',
        text: error?.message || 'Reaxys reaction results could not be opened.',
        confirmText: 'Close',
      });
      setBusy(false);
    }
  };

  const openRetrosynthesisSource = async (routePath) => {
    if (isSourceLoading || !routePath) return;
    onSourceClick?.();
    setBusy(true);
    try {
      await validateRetrosynthesisRouteSource({ projectId: run.sourceProjectId, routeId: run.sourceRouteId });
      setBusy(false);
      onBeforeNavigate?.(`conditions-source-rt-${run.id}`);
      history.push(routePath);
    } catch (error) {
      Logger.error('ConditionsSourceLink open Retrosynthesis source failed with error:', error);
      NotifyDialogsService.openModalWindow({
        type: 'error',
        title: 'Retrosynthesis project unavailable',
        text: `Project ${run.sourceProjectId} is no longer available`,
        confirmText: 'Close',
      });
      setBusy(false);
    }
  };

  const renderStandaloneSource = () => {
    const sourceLabel = run.referenceLabel || 'My drawn reaction';

    return showSourcePill ? <span className="rx-source-pill rx-source-pill--standalone">{sourceLabel}</span> : <span>{sourceLabel}</span>;
  };

  const renderHopintoSource = () => {
    const sourceLabel = run.referenceLabel || 'External link';

    return showSourcePill ? <span className="rx-source-pill rx-source-pill--standalone">{sourceLabel}</span> : <span>{sourceLabel}</span>;
  };

  const renderRetrosynthesisSource = () => {
    const routePath =
      run.sourceProjectId && run.sourceRouteId ? RsRoute.getProjectRouteTreeViewLink(run.sourceProjectId, run.sourceRouteId) : null;
    const href = routePath ? history.createHref({ pathname: routePath }) : null;
    const sourceLabel = getRetrosynthesisSourceLabel(run);

    return (
      <>
        {showSourcePill && <SourcePill modifier="rt">Retrosynthesis</SourcePill>}
        {href && sourceLabel ? (
          <LinkElement
            id={`conditions-source-rt-${run.id}`}
            data-e2e={retrosynthesisDataE2e}
            href={href}
            disabled={isSourceLoading}
            aria-label={`Open Retrosynthesis route for ${sourceLabel}`}
            onClick={(event) => {
              event.preventDefault();
              openRetrosynthesisSource(routePath);
            }}
            {...linkProps}>
            {sourceLabel}
          </LinkElement>
        ) : (
          <span>{sourceLabel}</span>
        )}
      </>
    );
  };

  const renderReaxysSource = () => {
    const sourceLabel = getReaxysSourceLabel(run);

    return (
      <>
        {showSourcePill && <SourcePill modifier="rx">Reaxys</SourcePill>}
        {run.sourceReactionId ? (
          <LinkElement
            id={`conditions-source-reaxys-${run.id}`}
            data-e2e={reaxysDataE2e}
            href="#"
            disabled={isSourceLoading}
            aria-label={`Open Reaxys reaction results for ${sourceLabel}`}
            onClick={(event) => {
              event.preventDefault();
              openReaxysSource();
            }}
            {...linkProps}>
            {sourceLabel}
          </LinkElement>
        ) : (
          <span>{sourceLabel}</span>
        )}
      </>
    );
  };

  let content = renderStandaloneSource();

  if (run.referenceType === 'reaxys_reaction') {
    content = renderReaxysSource();
  } else if (run.referenceType === 'retrosynthesis_step') {
    content = renderRetrosynthesisSource();
  } else if (run.referenceType === 'hopinto') {
    content = renderHopintoSource();
  }

  return className ? <div className={className}>{content}</div> : content;
}

ConditionsSourceLink.propTypes = {
  run: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    reference: PropTypes.string,
    referenceType: PropTypes.string,
    referenceLabel: PropTypes.string,
    sourceReactionId: PropTypes.string,
    sourceProjectId: PropTypes.string,
    sourceRouteId: PropTypes.string,
    sourceStepId: PropTypes.string,
  }).isRequired,
  className: PropTypes.string,
  showSourcePill: PropTypes.bool,
  linkProps: PropTypes.shape({}),
  reaxysDataE2e: PropTypes.string,
  retrosynthesisDataE2e: PropTypes.string,
  onBusyChange: PropTypes.func,
  onBeforeNavigate: PropTypes.func,
  onSourceClick: PropTypes.func,
};

export default ConditionsSourceLink;
